import React, { useMemo } from 'react';
import { DefaultMainContent, ListOption } from '@/components/list-item';
import {
  IngredientSearchResultMetadataType,
  MenuItem,
} from '@/contexts/menu-info-context';
import { ItemListOption } from '@/contexts/item-context-types';
import { ItemSubtitle } from '@/components/item-subtitle';
import { ManualIngredientMetadata } from './menu-item-search-types';
import { OrderableItemListItem } from '@/components/orders/orderable-item-list-item';
import { PlusButtonAction } from '@/components/plus-button-action';
import { StyleSheet, View } from 'react-native';

interface ManualIngredientListOptionType {
  handleIngredientSelection: (
    ingredientUUID: string | undefined,
    metadata: IngredientSearchResultMetadataType,
  ) => void;
  item: ListOption<ManualIngredientMetadata>;
  menuItem: MenuItem;
}

interface CatalogIngredientListOptionType {
  handleIngredientSelection: (
    ingredientUUID: string | undefined,
    metadata: IngredientSearchResultMetadataType,
  ) => void;
  item: ItemListOption;
  renderOrderGuideButton?: (variantPackUUID: string) => React.ReactNode;
  variantPackIngredientUUIDMap: Partial<Record<string, string>>;
}

const getPlusButtonStyle = (ingredientUUID: string | undefined) => {
  // Determine rotation degrees based on the presence of ingredientUUID
  // 0 degrees indicates the ingredient is to be added, 45 degrees indicates removal
  return {
    transform: [{ rotate: `${ingredientUUID ? 45 : 0}deg` }],
  };
};

export const ManualIngredientListOptionComponent: React.FC<
  ManualIngredientListOptionType
> = ({ handleIngredientSelection, item, menuItem }) => {
  const metadata = item.metadata!;
  const ingredientUUID = useMemo(() => {
    return menuItem.ingredients.find(
      (ing) => ing.ingredientUUID === metadata.menuItemIngredientUUID,
    )?.ingredientUUID;
  }, [menuItem.ingredients, metadata.menuItemIngredientUUID]);

  return (
    <View style={styles.manualIngredientContainer}>
      <DefaultMainContent item={item} />
      <View style={styles.flexRow}>
        <ItemSubtitle
          baseUnit={metadata.baseUnit}
          currencyCode={metadata.currencyCode || null}
          displayString={metadata.packDisplayString}
          showInlineModifiers={false}
          unitCount={metadata.unitCount}
          unitPriceInMicros={metadata.unitPriceInMicros}
        />
        <PlusButtonAction
          onPress={() => handleIngredientSelection(ingredientUUID, metadata)}
          style={getPlusButtonStyle(ingredientUUID)}
        />
      </View>
    </View>
  );
};

export const CatalogIngredientListOptionComponent: React.FC<
  CatalogIngredientListOptionType
> = ({
  item,
  variantPackIngredientUUIDMap,
  handleIngredientSelection,
  renderOrderGuideButton,
}) => (
  <OrderableItemListItem
    contentStyle={styles.content}
    hideSampleRequest
    isFocused={false}
    option={item}
    renderActions={(metadata) => {
      const ingredientUUID =
        variantPackIngredientUUIDMap[metadata.variantPackUUID];
      return (
        <PlusButtonAction
          onPress={() => handleIngredientSelection(ingredientUUID, metadata)}
          style={getPlusButtonStyle(ingredientUUID)}
        />
      );
    }}
    renderOrderGuideButton={renderOrderGuideButton}
    source="menu-item-ingredient"
  />
);

const styles = StyleSheet.create({
  content: {
    marginTop: 4,
  },
  flexRow: {
    flex: 1,
    flexDirection: 'row',
  },
  manualIngredientContainer: {
    flex: 1,
    marginBottom: 16,
    marginHorizontal: 16,
  },
});
